/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * fetchMyPasses
 * -------------
 * The passes this visitor holds, read through the event_pass public view.
 *
 * WHY THIS VIEW RATHER THAN THE TABLE ENDPOINTS. The view is owner scoped:
 * the server resolves the session's own application user and adds the
 * ownership clause itself, so the rows that come back are the visitor's and
 * nothing else — the browser does not say whose passes to fetch, and could not
 * ask for anybody else's. The ordinary read endpoints apply no row-level
 * security at all, which is why fetchMyRegistrations beside this file has to
 * name the person explicitly, and why a participant is refused on them
 * outright (event_pass is the only one of these tables that is public).
 *
 * It is also the read My Registrations already lives on, so a pass that shows
 * there shows here, with the same fields and the same QR.
 *
 * NO EVENT FILTER IS SENT, deliberately. The view's filters accept enum and
 * boolean columns only, so `event_id` is not filterable and asking for it
 * would be silently ignored — a filter that looks applied and is not is worse
 * than none. The caller narrows by event itself (passesForEvent), which is a
 * comparison over a handful of rows.
 *
 * IT FAILS TO "DO NOT KNOW", NEVER TO "NOTHING". Every failure returns null,
 * including the 401 an anonymous visitor gets, and callers draw nothing rather
 * than telling somebody they hold no passes when we simply could not ask.
 */

import { fetchPublicTableList, BINDING_ERROR } from '../../../hooks/usePublicTableView';

export const PASS_VIEW_SLUG = 'event_pass';

/** The largest page the public list endpoint will serve (MAX_PUBLIC_PAGE_SIZE). */
export const PAGE_SIZE = 100;

/**
 * How many pages to read at most.
 *
 * A thousand passes is far past any real holder, and the ceiling is here so a
 * server that keeps reporting more than it sends cannot turn this into an
 * endless loop on the event page's critical path.
 */
export const MAX_PAGES = 10;

/**
 * Every pass this visitor holds, newest first, or null when the question could
 * not be answered.
 *
 * The rows are returned as the server sends them, with their related `event`
 * attached by the view. Nothing is reshaped here: the rules module reads the
 * columns it needs through the same helpers the desk uses, so one change to a
 * column name does not have to be chased through a mapping layer as well.
 *
 * EVERY PAGE, NOT THE FIRST ONE. This used to read a single page and trust it
 * to hold everything, which was wrong in the way that hurts: the view orders
 * by "upcoming first", so an event that has already STARTED sorts to the back
 * — and that is precisely the event somebody is standing at the gate of. A
 * person with more passes than one page lost today's pass off the end, the
 * check-in never appeared, and nothing failed anywhere to say so. It also got
 * worse the longer somebody used the product, which is the worst shape a bug
 * can have.
 *
 * A LATER PAGE THAT FAILS KEEPS WHAT CAME BEFORE. Some passes are better than
 * none for a reader at a gate, and the one they need is as likely to be in
 * hand as not. Only a first page that fails means "could not ask".
 *
 * NO ABORT SIGNAL, on purpose: the params object becomes the query string, so
 * anything passed here that is not a value ends up in the URL. Callers that
 * unmount mid-flight drop the answer instead (see useMyPasses's `alive` flag).
 */
export default async function fetchMyPasses() {
  try {
    const first = await fetchPublicTableList(PASS_VIEW_SLUG, { page: 1, page_size: PAGE_SIZE });
    const rows = first?.records;
    if (!Array.isArray(rows)) return null;

    const all = rows.slice();
    const total = Number(first?.pagination?.total);
    for (let page = 2; page <= MAX_PAGES; page += 1) {
      if (!Number.isFinite(total) || all.length >= total) break;
      let next;
      try {
        next = await fetchPublicTableList(PASS_VIEW_SLUG, { page, page_size: PAGE_SIZE });
      } catch (later) {
        break;
      }
      const more = next?.records;
      // An empty page ends it whatever the total says: a total that disagrees
      // with the rows is the server's problem, and reading the same empty page
      // ten times would be ours.
      if (!Array.isArray(more) || more.length === 0) break;
      all.push(...more);
    }
    return all;
  } catch (error) {
    // Every other kind — auth, not_found, throttled, server, network — means
    // the same thing to a page that wanted to offer a check-in: we cannot say,
    // so offer nothing. The distinctions matter to a page whose whole job is
    // the list (My Registrations shows a sign-in invitation); here the section
    // simply does not appear, which is correct for all of them.
    return null;
  }
}

export { BINDING_ERROR };
